import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Save, Delete, Edit, CloudUpload } from '@mui/icons-material';
import { homeSliderService, extractApiDataList } from '../services/api';
import { getAssetUrl } from '../config';

const emptyForm = {
  subtitle: '',
  title: '',
  button_text: 'Donate Now',
  button_link: '/campaigns',
  display_order: '0',
  is_active: '1',
};

const HomeSlidersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const [newRow, setNewRow] = useState(emptyForm);
  const [newImage, setNewImage] = useState(null);
  const [newPreview, setNewPreview] = useState('');
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyForm);
  const [editImage, setEditImage] = useState(null);
  const [editPreview, setEditPreview] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await homeSliderService.getAll({ include_inactive: 1 });
      setSlides(extractApiDataList(res));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load slides';
      setLoadError(msg);
      setSlides([]);
      setToast({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickImage = (file, setFile, setPreview) => {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const buildFormData = (row, imageFile) => {
    const fd = new FormData();
    if (imageFile) fd.append('image', imageFile);
    fd.append('subtitle', row.subtitle || '');
    fd.append('title', row.title.trim());
    fd.append('button_text', row.button_text || 'Donate Now');
    fd.append('button_link', row.button_link || '/campaigns');
    fd.append('display_order', String(row.display_order === '' ? 0 : row.display_order));
    fd.append('is_active', row.is_active === '1' ? '1' : '0');
    return fd;
  };

  const handleAdd = async () => {
    if (!newRow.title.trim()) {
      setToast({ open: true, message: 'Title is required', type: 'error' });
      return;
    }
    if (!newImage) {
      setToast({ open: true, message: 'Please upload a slide image', type: 'error' });
      return;
    }
    try {
      await homeSliderService.create(buildFormData(newRow, newImage));
      setNewRow(emptyForm);
      setNewImage(null);
      setNewPreview('');
      setToast({ open: true, message: 'Slide added', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditImage(null);
    setEditPreview(getAssetUrl(row.image_url));
    setEditRow({
      subtitle: row.subtitle || '',
      title: row.title || '',
      button_text: row.button_text || 'Donate Now',
      button_link: row.button_link || '/campaigns',
      display_order: String(row.display_order ?? 0),
      is_active: row.is_active === 0 || row.is_active === false ? '0' : '1',
    });
  };

  const handleSaveEdit = async () => {
    if (!editId || !editRow.title.trim()) return;
    try {
      await homeSliderService.update(editId, buildFormData(editRow, editImage));
      setEditId(null);
      setEditImage(null);
      setEditPreview('');
      setToast({ open: true, message: 'Slide updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    try {
      await homeSliderService.delete(id);
      setToast({ open: true, message: 'Slide deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Home Page Slider
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Manage hero carousel images, headlines, button text, and links shown on the public home page.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add new slide
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                Upload image *
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => onPickImage(e.target.files?.[0], setNewImage, setNewPreview)}
                />
              </Button>
              {newPreview && (
                <Box component="img" src={newPreview} alt="Preview" sx={{ mt: 1, width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 1 }} />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Subtitle (small line)" value={newRow.subtitle} onChange={(e) => setNewRow((s) => ({ ...s, subtitle: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Headline *" value={newRow.title} onChange={(e) => setNewRow((s) => ({ ...s, title: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Button text" value={newRow.button_text} onChange={(e) => setNewRow((s) => ({ ...s, button_text: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" label="Button link" value={newRow.button_link} onChange={(e) => setNewRow((s) => ({ ...s, button_link: e.target.value }))} helperText="e.g. /campaigns or /campaign/school-kit or https://..." />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Order" type="number" value={newRow.display_order} onChange={(e) => setNewRow((s) => ({ ...s, display_order: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" select label="Active" value={newRow.is_active} onChange={(e) => setNewRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add slide
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell>Preview</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Button</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slides.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#667085' }}>
                      No slides yet. Add one above or run the SQL migration.
                    </TableCell>
                  </TableRow>
                ) : (
                  slides.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {editId === row.id ? (
                          <Box>
                            <Button size="small" component="label" startIcon={<CloudUpload />}>
                              Change image
                              <input type="file" hidden accept="image/*" onChange={(e) => onPickImage(e.target.files?.[0], setEditImage, setEditPreview)} />
                            </Button>
                            {editPreview && <Avatar variant="rounded" src={editPreview} sx={{ mt: 1, width: 120, height: 72 }} />}
                          </Box>
                        ) : (
                          <Avatar variant="rounded" src={getAssetUrl(row.image_url)} sx={{ width: 120, height: 72 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {editId === row.id ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField size="small" label="Subtitle" value={editRow.subtitle} onChange={(e) => setEditRow((s) => ({ ...s, subtitle: e.target.value }))} />
                            <TextField size="small" label="Headline" value={editRow.title} onChange={(e) => setEditRow((s) => ({ ...s, title: e.target.value }))} />
                          </Box>
                        ) : (
                          <Box>
                            {row.subtitle && (
                              <Typography variant="caption" sx={{ color: '#667085', display: 'block' }}>
                                {row.subtitle}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.title}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField size="small" label="Button text" value={editRow.button_text} onChange={(e) => setEditRow((s) => ({ ...s, button_text: e.target.value }))} />
                            <TextField size="small" label="Link" value={editRow.button_link} onChange={(e) => setEditRow((s) => ({ ...s, button_link: e.target.value }))} />
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2">{row.button_text || '—'}</Typography>
                            <Typography variant="caption" sx={{ color: '#667085' }}>
                              {row.button_link}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" type="number" value={editRow.display_order} onChange={(e) => setEditRow((s) => ({ ...s, display_order: e.target.value }))} />
                        ) : (
                          row.display_order
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" select value={editRow.is_active} onChange={(e) => setEditRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                          </TextField>
                        ) : (
                          <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" color={row.is_active ? 'success' : 'default'} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editId === row.id ? (
                          <>
                            <IconButton size="small" color="primary" onClick={handleSaveEdit}>
                              <Save fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setEditId(null)}>
                              ×
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton size="small" onClick={() => startEdit(row)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomeSlidersPage;
